import { Button, Card, Checkbox, DatePicker, Form, Select, Space } from 'antd';
import { getDailyStatistics, getMonthlySalesStatistics } from 'apis/report';
import LOCALS from 'commons/locals';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useToggle } from 'react-use';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import {
  DailyStatisticsType,
  MonthlySalesStatisticsType,
  OrderStatisticsType,
} from 'types/report';
import i18n from 'i18n';
import { thousands } from 'utils/tools';
import { formatCurrencyByStockPlace } from 'utils/stock-place-currency';
import exportToExcel from 'utils/exportToExcel';
import SalesDailyReport from './components/SalesDailyReport';
import PurchaseDailyReport, {
  PurchaseDailyReportRef,
} from './components/PurchaseDailyReport';
import ConsignmentDailyReport, {
  ConsignmentDailyReportRef,
} from './components/ConsignmentDailyReport';
import { ChartColumnStacked } from 'lucide-react';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { STOCK_PLACE_OPTION_LIST } from 'constant/stock-place';
import {
  getOrderCreateSourceConfig,
  ORDER_CREATE_SOURCE_MAP,
  ORDER_CREATED_FROM_OPTION_LIST,
} from 'constant/order-create-source';
import { PRODUCT_SOURCE_TYPE_OPTION_LIST } from 'constant/pms-product';

const Statement = () => {
  const [dailyData, setDailyData] = useState<DailyStatisticsType>();
  const purchaseRef = useRef<PurchaseDailyReportRef>(null);
  const consignmentRef = useRef<ConsignmentDailyReportRef>(null);
  const [loading, toggleLoading] = useToggle(false);
  const [monthlyData, setMonthlyData] = useState<MonthlySalesStatisticsType[]>(
    [],
  );
  const { staffSelectOptions } = useAppSelector(selectGlobalInfo);
  const [date, setDate] = useQueryState(
    'date',
    parseAsString.withDefault(dayjs().startOf('day').format()),
  );
  const [createdFrom, setCreatedFrom] = useQueryState(
    'createdFrom',
    parseAsInteger,
  );
  const [month, setMonth] = useQueryState(
    'month',
    parseAsString.withDefault(dayjs().startOf('month').format()),
  );
  const [monthlyCreatedFrom, setMonthlyCreatedFrom] = useQueryState(
    'monthlyCreatedFrom',
    parseAsArrayOf(parseAsInteger).withDefault([]),
  );
  const [excludeB2B, setExcludeB2B] = useQueryState(
    'excludeB2B',
    parseAsBoolean.withDefault(false),
  );
  const [stockPlace, setStockPlace] = useQueryState(
    'stockPlace',
    parseAsString,
  );

  const currencySwitch = useCallback(
    (value: number) => {
      return formatCurrencyByStockPlace(value, stockPlace || undefined);
    },
    [stockPlace],
  );

  // 自定义 Tooltip 组件，显示总数
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      // 计算所有值的总和
      const total = payload.reduce(
        (sum: number, entry: any) => sum + (entry.value || 0),
        0,
      );

      return (
        <div
          style={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px',
          }}
        >
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: '4px 0' }}>
              <span style={{ color: entry.color }}>●</span> {entry.name}: JPY{' '}
              {entry.value.toLocaleString()}
            </p>
          ))}
          <div
            style={{
              borderTop: '1px solid #ddd',
              marginTop: '8px',
              paddingTop: '8px',
            }}
          >
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              Total: JPY {total.toLocaleString()}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const fetchData = useCallback(() => {
    const toDay = dayjs().startOf('day').format();
    toggleLoading(true);
    getDailyStatistics({
      date: date || toDay,
      createdFrom: createdFrom === null ? undefined : createdFrom,
      stockPlace: stockPlace === null ? undefined : stockPlace,
    })
      .then((res) => {
        res.orderStatistics.orders.forEach((order) => {
          if (!order.staffName && order.createdBy) {
            order.staffName =
              staffSelectOptions.find((d) => d.value === order.createdBy)
                ?.label || '';
          }
        });

        // 按 staffName 排序
        res.orderStatistics.orders.sort((a, b) =>
          (b.staffName || '').localeCompare(a.staffName || ''),
        );

        setDailyData(res);
      })
      .finally(() => {
        toggleLoading(false);
      });
  }, [createdFrom, date, staffSelectOptions, stockPlace, toggleLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    getMonthlySalesStatistics({ date: month, excludeB2B }).then((res) => {
      setMonthlyData(res);
    });
  }, [excludeB2B, month, setMonthlyData]);

  const monthlyBars = useMemo(
    () => [
      {
        key: ORDER_CREATE_SOURCE_MAP.WEBSITE,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.WEBSITE).label,
        fill: '#1f77b4',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.GINZA,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.GINZA).label,
        fill: '#ff7f0e',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.HONGKONG,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.HONGKONG)
          .label,
        fill: '#2ca02c',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.SINGAPORE,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.SINGAPORE)
          .label,
        fill: '#d62728',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.SINGAPORE_GX,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.SINGAPORE_GX)
          .label,
        fill: '#07E39A',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.SNS,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.SNS).label,
        fill: '#9467bd',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.WHATSAPP,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.WHATSAPP)
          .label,
        fill: '#8c564b',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.INSTAGRAM,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.INSTAGRAM)
          .label,
        fill: '#e377c2',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.LINE,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.LINE).label,
        fill: '#7f7f7f',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.WECHAT,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.WECHAT).label,
        fill: '#bcbd22',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.OTHER,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.OTHER).label,
        fill: '#17becf',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.APP,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.APP).label,
        fill: '#ff69b4',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.SHANG_HAI,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.SHANG_HAI)
          .label,
        fill: '#1e90ff',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.UMEDA,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.UMEDA).label,
        fill: '#a52a2a',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.DUBAI,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.DUBAI).label,
        fill: '#ffd700',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.FRANCE,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.FRANCE).label,
        fill: '#6a0dad',
      },
      {
        key: ORDER_CREATE_SOURCE_MAP.NEW_YORK,
        name: getOrderCreateSourceConfig(ORDER_CREATE_SOURCE_MAP.NEW_YORK)
          .label,
        fill: '#4169e1',
      },
    ],
    [],
  );

  const visibleMonthlyBars = useMemo(() => {
    if (!monthlyCreatedFrom.length) return monthlyBars;
    return monthlyBars.filter((bar) => monthlyCreatedFrom.includes(bar.key));
  }, [monthlyBars, monthlyCreatedFrom]);

  const monthlyTotalSales = useMemo(() => {
    if (!monthlyData.length) return 0;
    const keys = visibleMonthlyBars.map((b) => b.key);
    return monthlyData.reduce((sum, row) => {
      const rowTotal = keys.reduce((acc: number, key: number) => {
        const v = row[key as unknown as keyof MonthlySalesStatisticsType];
        return acc + (Number(v) || 0);
      }, 0);
      return sum + rowTotal;
    }, 0);
  }, [monthlyData, visibleMonthlyBars]);

  const columnsRender = useCallback(
    () => ({
      salesPaymentMethod: (
        paymentMethod: Record<string, number>,
        record: OrderStatisticsType,
      ) => {
        const entries = Object.entries(paymentMethod || {});
        if (entries.length === 0) return '';
        return entries.length === 1
          ? entries[0][0]
          : entries
              .map(
                ([method, amount]) =>
                  `${method}: ${record.currency} ${thousands(amount)}`,
              )
              .join(', ');
      },
    }),
    [],
  );

  // 添加订单编码的表头
  const customHeaders = useMemo(
    () => [
      i18n.t('sales_date'),
      i18n.t('order_sn'),
      i18n.t('sales_product_id'),
      i18n.t('sales_quantity_sold'),
      i18n.t('currency'),
      i18n.t('sales_sales_amount_(subtotal)'),
      i18n.t('purchase_price'),
      // i18n.t('gross_profit'),
      i18n.t('sales_total'),
      i18n.t('sales_tax'),
      i18n.t('sales_payment_method'),
      i18n.t('product_source'),
      i18n.t('sales_responsible'),
      i18n.t('sales_notes'),
    ],
    [],
  );
  const getSourceTypeText = (sourceType: number) => {
    const option = PRODUCT_SOURCE_TYPE_OPTION_LIST.find(
      (item) => item.value === sourceType,
    );
    return option ? option.label : '-';
  };
  const formatProductsAndSource = useCallback(
    (products: Array<{ productId: number; sourceType: number }>) => () => {
      // 如果只有一个商品，只显示来源
      if (products.length === 1) {
        return getSourceTypeText(products[0].sourceType);
      }

      // 如果有多个商品，显示 "商品ID: 来源" 的格式
      return products
        .map(
          (item) => `${item.productId}: ${getSourceTypeText(item.sourceType)}`,
        )
        .join(', ');
    },
    [],
  );

  // 提取销售数据处理逻辑
  const getSalesData = useCallback(() => {
    if (
      !dailyData ||
      !dailyData.orderStatistics ||
      !dailyData?.orderStatistics?.orders
    )
      return [];

    return dailyData.orderStatistics.orders.map((d) => {
      const paymentMethod = columnsRender().salesPaymentMethod(
        d.paymentMethod,
        d,
      );
      const staffName = d.staffName || '';
      const productIdsStr = d.productIds
        .map((item) => item.productId)
        .join(',');

      const sourceTypesStr = formatProductsAndSource(d.productIds);

      return [
        dayjs(d.date).format('YYYY-MM-DD'),
        d.orderSn,
        productIdsStr,
        d.salesQuantity,
        d.currency,
        `${thousands(d.payAmountActualCurrency)}`,
        `${thousands(d.totalCost)}`,
        // `${thousands(d.grossProfit)}`,
        `${thousands(d.totalAmountActualCurrency)}`,
        `${thousands(d.taxAmount)}`,
        paymentMethod,
        sourceTypesStr(),
        staffName,
        d.note,
      ];
    });
  }, [dailyData, columnsRender, formatProductsAndSource]);

  const dlExcel = useCallback(() => {
    if (staffSelectOptions.length === 0 && dailyData?.orderStatistics?.orders)
      return;

    const salesSheet = {
      sheetName: i18n.t('sales_daily_report'),
      data: [
        customHeaders, // 表头
        ...getSalesData(),
      ],
    };

    const purchaseSheetData = purchaseRef.current?.getSheetData();
    const purchaseSheet = {
      sheetName: i18n.t('purchase_daily_report'),
      data: [
        purchaseSheetData?.headers || [],
        ...(purchaseSheetData?.rows || []),
        [], // 换两行
        [],
        ...(purchaseSheetData?.stats || []),
      ],
    };

    const consignmentSheetData = consignmentRef.current?.getSheetData();
    const consignmentSheet = {
      sheetName: i18n.t('consignment_daily_report'),
      data: [
        consignmentSheetData?.headers || [],
        ...(consignmentSheetData?.rows || []),
        [], // 换两行
        [],
        ...(consignmentSheetData?.stats || []),
      ],
    };

    exportToExcel(
      [salesSheet, purchaseSheet, consignmentSheet],
      `${i18n.t('nikkei_table')}_${dayjs(date).format('YYYYMMDD')}.xlsx`,
    );
  }, [
    staffSelectOptions.length,
    dailyData?.orderStatistics?.orders,
    customHeaders,
    date,
    getSalesData,
  ]);

  return (
    <div className="space-y-4">
      <Card bodyStyle={{ padding: '16px' }}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="flex items-center gap-2 text-base font-medium">
            <ChartColumnStacked className="w-5 h-5" />
            <Trans i18nKey={LOCALS.HMCYidBcAe} />
            <span className="text-sm font-bold">
              {`Total: JPY ${monthlyTotalSales.toLocaleString()}`}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <DatePicker
              className="w-full sm:w-40"
              picker="month"
              value={dayjs(month)}
              onChange={(value) => {
                if (value) {
                  setMonth(value.startOf('month').format());
                } else {
                  setMonth(null);
                }
              }}
            />
            <Select
              className="w-full sm:w-52"
              mode="multiple"
              value={monthlyCreatedFrom}
              onChange={(value) => {
                setMonthlyCreatedFrom(value);
              }}
              placeholder={i18n.t(LOCALS.order_source)}
              options={ORDER_CREATED_FROM_OPTION_LIST}
              allowClear
            />
            <div className="flex h-8 items-center whitespace-nowrap">
              <Checkbox
                checked={excludeB2B}
                onChange={(event) => {
                  setExcludeB2B(event.target.checked);
                }}
              >
                <Trans i18nKey={LOCALS.exclude_b2b_orders} />
              </Checkbox>
            </div>
          </div>
        </div>

        <div className="h-[320px] sm:h-[420px] lg:h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={monthlyData}
              margin={{
                left: 48,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis
                tickFormatter={(value) => `JPY\u00A0${value.toLocaleString()}`}
              />

              <Tooltip content={<CustomTooltip />} />

              {visibleMonthlyBars.map((bar) => (
                <Bar
                  key={bar.key}
                  dataKey={bar.key}
                  name={bar.name}
                  fill={bar.fill}
                  stackId="a"
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card bodyStyle={{ padding: '16px' }}>
        <div className="w-full flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <Form
            layout={'inline'}
            className="flex flex-col gap-3 sm:flex-row sm:flex-wrap flex-1"
          >
            <Form.Item
              label={<Trans i18nKey={LOCALS.date} />}
              className="w-full sm:w-auto"
            >
              <DatePicker
                className="w-full sm:w-auto"
                value={dayjs(date)}
                onChange={(value) => {
                  if (value) {
                    setDate(value.startOf('day').format());
                  } else {
                    setDate(null);
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              label={<Trans i18nKey={LOCALS.order_source} />}
              className="w-64"
            >
              <Select
                className="w-full"
                value={createdFrom}
                onChange={(value) => {
                  if (value !== undefined) {
                    setCreatedFrom(value);
                  } else {
                    setCreatedFrom(null);
                  }
                }}
                placeholder={i18n.t(LOCALS.please_select)}
                options={ORDER_CREATED_FROM_OPTION_LIST}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label={<Trans i18nKey={LOCALS.stock_place} />}
              className="w-64"
            >
              <Select
                className="w-full"
                value={stockPlace}
                onChange={(value) => {
                  if (value !== undefined) {
                    setStockPlace(value);
                  } else {
                    setStockPlace(null);
                  }
                }}
                placeholder={i18n.t(LOCALS.please_select)}
                options={STOCK_PLACE_OPTION_LIST}
                allowClear
              />
            </Form.Item>
            <Form.Item className="w-full sm:w-auto">
              <Space className="w-full sm:w-auto">
                <Button
                  className="w-full sm:w-auto"
                  htmlType="button"
                  onClick={() => {
                    setDate(null);
                    setCreatedFrom(null);
                    setStockPlace(null);
                  }}
                >
                  <Trans i18nKey={LOCALS.reset} />
                </Button>
              </Space>
            </Form.Item>
          </Form>
          <div className="flex justify-end lg:justify-start">
            <Button className="w-full sm:w-auto" onClick={() => dlExcel()}>
              {i18n.t('download_excel')}
            </Button>
          </div>
        </div>
      </Card>

      <SalesDailyReport
        loading={loading}
        orders={dailyData?.orderStatistics?.orders}
        summary={dailyData?.orderStatistics?.summary}
        stockPlace={stockPlace === null ? undefined : stockPlace}
      />

      <PurchaseDailyReport
        ref={purchaseRef}
        loading={loading}
        records={dailyData?.purchaseReport.records}
        statistics={dailyData?.purchaseReport?.statistics}
        currencySwitch={currencySwitch}
      />

      <ConsignmentDailyReport
        ref={consignmentRef}
        loading={loading}
        records={dailyData?.consignmentReport.records}
        statistics={dailyData?.consignmentReport?.statistics}
        currencySwitch={currencySwitch}
      />
    </div>
  );
};
export default Statement;
